import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { COLORS } from '../../config/designSystem/Colors';
import { DropShadowLightCloseStyle } from '../../config/designSystem/Shadow';
import { Modal, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { SPACING } from '../../config/designSystem/Spacing';
import { TooltipProps } from './TooltipProps';
import { observer } from 'mobx-react';
import { useIsFocused } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Replaces react-native-walkthrough-tooltip, which is unmaintained, ships no
// codegenConfig and therefore runs through the legacy interop layer on the New
// Architecture. There its modal mis-measured: the container collapsed to a sliver
// over the status bar while still swallowing every touch, which froze the app after
// selecting a program. This is built only on core RN primitives, so it measures
// correctly under Fabric.
//
// The overlay never re-renders `from`. `from` stays mounted in place and the dim is
// drawn as four rects AROUND it, leaving a hole the real element shows through. That
// avoids mounting the highlighted subtree twice (double effects, duplicate state).

/** Breathing room between the highlighted element and the edge of the hole. */
const HOLE_PADDING = SPACING['sp-8'];
/** Gap between the hole and the bubble. */
const BUBBLE_GAP = SPACING['sp-12'];
/** Keeps the bubble off the screen edges. */
const SCREEN_MARGIN = SPACING['sp-16'];
// Corner radius of the highlighted cut-out. Kept equal to styles.ring so the dimmed
// layer and the ring drawn on top of it describe the same rounded box.
const HOLE_RADIUS = SPACING['sp-12'];
const SCRIM_COLOR = 'rgba(20, 22, 43, 0.55)';

type Rect = { x: number; y: number; width: number; height: number };

const Tooltip: FC<TooltipProps> = props => {
  const { isVisible, placement = 'bottom', from, onClose, onCloseComplete, children, highlight = true } = props;
  const { width: screenW, height: screenH } = useWindowDimensions();
  // This overlay is a native <Modal>, so it renders in its own window, OUTSIDE the root
  // view that reserves the system-bar insets for the rest of the app. Clamping the bubble
  // against the raw screen box would let a coach mark anchored low on screen sit behind
  // the Android navigation bar (48dp, against a 16dp SCREEN_MARGIN), or one anchored high
  // sit under the status bar. Clamp inside the safe area instead.
  const insets = useSafeAreaInsets();
  // Thicker than the screen in any direction, so the border covers everything outside
  // the hole no matter where on screen the anchor sits.
  const scrimSpread = Math.max(screenW, screenH);

  // A coach mark is driven by state that lives on a SCREEN, and a screen stays mounted
  // when another one is pushed over it or when the user switches tab. Without this guard
  // a background screen keeps presenting its own native Modal: two Modals then stack, only
  // the topmost window receives touches, and the one underneath can never be reached to
  // dismiss it -- which is exactly the "two tooltips, one white one greyed, nothing is
  // clickable" freeze. Only the overlay is withheld while blurred; `from` still renders.
  const isFocused = useIsFocused();

  // The overlay closes itself. `isVisible` is fully controlled by the caller and some
  // callers only clear it after a server round trip (path.tsx clears it from the
  // setVisitedPages mutation). If that call is slow, offline or rejected the overlay would
  // stay up forever while its full-screen Pressable eats every touch in the app. Closing
  // locally guarantees one tap always gets the user out.
  const [dismissed, setDismissed] = useState(false);

  const anchorRef = useRef<View>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [bubble, setBubble] = useState<{ width: number; height: number } | null>(null);
  // Whether the anchor has been measured since THIS opening. A tour step reuses the
  // component with a position that may be stale (the previous step scrolled the list),
  // so painting before the fresh measure lands made the spotlight appear over the wrong
  // element for a frame and then jump to the right one.
  const [measured, setMeasured] = useState(false);
  // If the anchor never reports a size (a Show-guarded icon that rendered nothing, say),
  // stop waiting after a beat and fall back to the centered presentation rather than
  // leaving an invisible, un-dismissable overlay.
  const [settled, setSettled] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Measured on layout and again whenever the overlay opens, so a scroll between
  // steps can't leave us pointing at a stale position.
  const measure = useCallback(() => {
    anchorRef.current?.measureInWindow((x, y, width, height) => {
      if (width || height) {
        setRect({ x, y, width, height });
        setMeasured(true);
      }
    });
  }, []);

  const close = () => {
    setDismissed(true);
    onClose?.();
    // Let the fade finish before reporting completion, so callers can use
    // onCloseComplete as a real "transition is over" signal. Kept in a ref and
    // cleared on unmount so it can never fire into an unmounted tree.
    closeTimer.current = setTimeout(() => onCloseComplete?.(), 200);
  };

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  // A fresh opening (the caller flipping `isVisible` back on) clears the local dismissal.
  useEffect(() => {
    if (isVisible) setDismissed(false);
  }, [isVisible]);

  const open = !!isVisible && isFocused && !dismissed;

  // Invalidate the previous measurement the moment the overlay is asked to open, so
  // nothing is painted from a stale rect while the fresh measure is in flight.
  useEffect(() => {
    if (!open) return;
    setMeasured(false);
    setSettled(false);
    measure();
    settleTimer.current = setTimeout(() => setSettled(true), 250);
    return () => { if (settleTimer.current) clearTimeout(settleTimer.current); };
  }, [open, measure]);

  const hole = measured && rect && {
    x: rect.x - HOLE_PADDING,
    y: rect.y - HOLE_PADDING,
    width: rect.width + 2 * HOLE_PADDING,
    height: rect.height + 2 * HOLE_PADDING,
  };

  const bubblePosition = () => {
    // Anchor never measured (zero-size `from`, e.g. a Show-guarded icon that
    // rendered nothing): fall back to a centered bubble rather than an invisible
    // one behind a full-screen scrim.
    // Not measured yet, or the bubble has not been laid out: stay invisible rather
    // than flashing in the wrong place.
    if (!bubble || (!measured && !settled)) return { opacity: 0 as const, top: 0, left: 0 };
    if (!hole) {
      return {
        top: Math.max(insets.top + SCREEN_MARGIN, screenH / 2 - bubble.height / 2),
        left: Math.max(SCREEN_MARGIN, screenW / 2 - bubble.width / 2),
      };
    }

    let top: number;
    let left: number;

    switch (placement) {
    case 'top':
      top = hole.y - bubble.height - BUBBLE_GAP;
      left = hole.x + hole.width / 2 - bubble.width / 2;
      break;
    case 'left':
      top = hole.y + hole.height / 2 - bubble.height / 2;
      left = hole.x - bubble.width - BUBBLE_GAP;
      break;
    case 'right':
      top = hole.y + hole.height / 2 - bubble.height / 2;
      left = hole.x + hole.width + BUBBLE_GAP;
      break;
    case 'center':
      top = screenH / 2 - bubble.height / 2;
      left = screenW / 2 - bubble.width / 2;
      break;
    default:
      top = hole.y + hole.height + BUBBLE_GAP;
      left = hole.x + hole.width / 2 - bubble.width / 2;
    }

    // Clamp so a bubble anchored near an edge stays fully on screen. Without this
    // a highlight in a corner pushes half the text off the display.
    const clamp = (v: number, min: number, max: number) =>
      Math.max(min, Math.min(v, max));
    return {
      top: clamp(
        top,
        insets.top + SCREEN_MARGIN,
        screenH - insets.bottom - bubble.height - SCREEN_MARGIN,
      ),
      left: clamp(left, SCREEN_MARGIN, screenW - bubble.width - SCREEN_MARGIN),
    };
  };

  return (
    <>
      <View ref={anchorRef} collapsable={false} onLayout={measure}>
        {from}
      </View>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        statusBarTranslucent
        onShow={measure}
        onRequestClose={close}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={close}>
          {!!hole && (
            <>
              {/* One dim layer with a ROUNDED hole punched in it, rather than four rects
                  around a rectangular gap -- those left square corners poking out around
                  the rounded ring (and around the rounded cards it highlights). A view
                  whose border is thicker than the screen paints everything outside the
                  hole, and its inner edge takes HOLE_RADIUS, matching styles.ring. */}
              <View
                pointerEvents="none"
                style={[
                  styles.scrimWithHole,
                  {
                    top: hole.y - scrimSpread,
                    left: hole.x - scrimSpread,
                    width: hole.width + 2 * scrimSpread,
                    height: hole.height + 2 * scrimSpread,
                    borderWidth: scrimSpread,
                    borderRadius: scrimSpread + HOLE_RADIUS,
                  },
                ]}
              />

              {highlight && (
                <View
                  pointerEvents="none"
                  style={[styles.ring, { top: hole.y, left: hole.x, width: hole.width, height: hole.height }]}
                />
              )}
            </>
          )}

          {/* Only dim the whole screen once we know there is no hole to cut -- dimming
              first and cutting the hole a frame later is itself a visible jump. */}
          {!hole && settled && <View style={[StyleSheet.absoluteFill, styles.scrim]} />}

          <View
            style={[styles.bubble, bubblePosition()]}
            onLayout={e => {
              const { width, height } = e.nativeEvent.layout;
              if (width !== bubble?.width || height !== bubble?.height) setBubble({ width, height });
            }}
          >
            {children}
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

export default observer(Tooltip);

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    backgroundColor: SCRIM_COLOR,
  },
  scrimWithHole: {
    position: 'absolute',
    borderColor: SCRIM_COLOR,
  },
  ring: {
    position: 'absolute',
    borderRadius: HOLE_RADIUS,
    borderWidth: 2,
    borderColor: COLORS.border.accent,
  },
  bubble: {
    position: 'absolute',
    maxWidth: 320,
    borderLeftWidth: SPACING['sp-4'],
    borderColor: COLORS.border.accent,
    borderRadius: SPACING['sp-12'],
    backgroundColor: COLORS.surface.contrast,
    padding: SPACING['sp-12'],
    ...DropShadowLightCloseStyle,
  },
});
